import { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Upload, Sparkles, Download, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import { api, formatApiErrorDetail } from "../lib/api";
import { getDeviceId } from "../lib/device";

const LOADING_LINES = [
  "Steaming the fabric…",
  "Adjusting the drape to your frame…",
  "Matching the light of your photo…",
  "Setting the final stitches…",
];

export default function TryOn() {
  const { id } = useParams();
  const [item, setItem] = useState(null);
  const [photo, setPhoto] = useState(null); // data url
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lineIdx, setLineIdx] = useState(0);
  const fileRef = useRef();

  useEffect(() => {
    api.get(`/catalog/${id}`).then(({ data }) => setItem(data)).catch(() => setItem(false));
  }, [id]);

  useEffect(() => {
    if (!loading) return;
    const t = setInterval(() => setLineIdx((i) => (i + 1) % LOADING_LINES.length), 2200);
    return () => clearInterval(t);
  }, [loading]);

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Please choose an image under 8MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setPhoto(reader.result);
      setResult(null);
    };
    reader.readAsDataURL(file);
  };

  const generate = async () => {
    if (!photo) {
      toast.error("Upload a photo of yourself first.");
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/tryon", { garment_id: id, photo_base64: photo, device_id: getDeviceId() });
      const gid = data.generation_id;
      if (!gid) throw new Error("Could not start try-on.");

      // Poll The New Black until the fitting is ready (up to ~2 minutes).
      let done = false;
      for (let i = 0; i < 60 && !done; i++) {
        await new Promise((r) => setTimeout(r, 3000));
        const { data: st } = await api.get(`/tryon/status/${gid}`);
        if (st.status === "succeeded" && st.result_image) {
          setResult(st.result_image);
          toast.success("Your fitting is ready.");
          done = true;
        } else if (st.status === "failed" || st.status === "error") {
          throw new Error("The try-on could not be generated. Try another photo.");
        }
      }
      if (!done) throw new Error("This is taking longer than usual. Please try again.");
    } catch (err) {
      toast.error(err.response ? formatApiErrorDetail(err.response?.data?.detail) : err.message);
    } finally {
      setLoading(false);
    }
  };

  if (item === null) {
    return (
      <div className="relative z-10 min-h-screen">
        <Navbar />
        <p className="px-16 py-20 font-serif-display text-2xl italic text-[#8B5A2B]">Loading…</p>
      </div>
    );
  }

  return (
    <div className="relative z-10 min-h-screen">
      <Navbar />
      <div className="px-6 md:px-16 py-10">
        <Link to={`/catalog/${id}`} data-testid="back-to-detail" className="inline-flex items-center gap-2 text-sm text-[#4A4A4A] hover:text-[#6D214F] transition-colors mb-8">
          <ArrowLeft className="w-4 h-4" /> Back to {item?.title}
        </Link>

        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-start">
          {/* Left: inputs */}
          <div>
            <p className="text-xs uppercase tracking-[0.4em] text-[#8B5A2B] mb-3">The Fitting Room</p>
            <h1 className="font-serif-display text-4xl leading-tight mb-8">Let's see {item?.title} on you.</h1>

            <div className="flex items-center gap-4 mb-8 p-4 bg-[#F4F1EA] rounded-sm border border-[#1A1A1A]/10">
              <img src={item?.image} alt="" className="w-16 h-20 object-cover rounded-sm" />
              <div>
                <h3 className="font-serif-display text-lg">{item?.title}</h3>
                <p className="text-xs uppercase tracking-[0.2em] text-[#8B5A2B]">{item?.era} · {item?.category}</p>
              </div>
            </div>

            <input ref={fileRef} data-testid="photo-input" type="file" accept="image/*" onChange={onFile} className="hidden" />

            {photo ? (
              <div className="relative group">
                <img src={photo} alt="you" className="w-full max-h-[360px] object-contain bg-[#F4F1EA] rounded-sm border border-[#1A1A1A]/10" />
                <button
                  data-testid="change-photo"
                  onClick={() => fileRef.current?.click()}
                  className="absolute top-3 right-3 bg-[#FDFBF7]/90 backdrop-blur text-sm px-4 py-2 rounded-sm border border-[#1A1A1A]/10 hover:bg-[#FDFBF7] transition-colors"
                >
                  Change photo
                </button>
              </div>
            ) : (
              <button
                data-testid="upload-photo"
                onClick={() => fileRef.current?.click()}
                className="w-full border border-dashed border-[#1A1A1A]/25 rounded-sm py-16 flex flex-col items-center justify-center text-[#4A4A4A] hover:border-[#6D214F] hover:text-[#6D214F] transition-colors duration-300"
              >
                <Upload className="w-7 h-7 mb-4" strokeWidth={1.4} />
                <span className="text-sm">Upload a photo of yourself</span>
                <span className="text-xs mt-1 text-[#8B5A2B]">Full or half body, good lighting, facing forward</span>
              </button>
            )}

            <button
              data-testid="generate-tryon"
              onClick={generate}
              disabled={loading || !photo}
              className="w-full mt-6 bg-[#1A1A1A] text-[#FDFBF7] py-4 rounded-sm hover:bg-[#6D214F] transition-colors duration-300 disabled:opacity-50 inline-flex items-center justify-center gap-3"
            >
              <Sparkles className="w-4 h-4" />
              {loading ? "Tailoring…" : result ? "Try again" : "Render my fitting"}
            </button>
          </div>

          {/* Right: result */}
          <div className="lg:sticky lg:top-28">
            <div className="aspect-[3/4] rounded-sm border border-[#1A1A1A]/10 bg-[#F4F1EA] overflow-hidden flex items-center justify-center relative">
              <AnimatePresence mode="wait">
                {loading ? (
                  <motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="text-center px-8">
                    <div className="w-10 h-10 border-2 border-[#8B5A2B]/30 border-t-[#6D214F] rounded-full animate-spin mx-auto mb-6" />
                    <p className="font-serif-display text-2xl italic text-[#6D214F]">{LOADING_LINES[lineIdx]}</p>
                  </motion.div>
                ) : result ? (
                  <motion.img
                    key="result"
                    data-testid="tryon-result"
                    initial={{ opacity: 0, scale: 1.03 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.8 }}
                    src={result}
                    alt="You wearing the piece"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center px-10 text-[#4A4A4A]">
                    <Sparkles className="w-8 h-8 mx-auto mb-4 text-[#8B5A2B]" strokeWidth={1.2} />
                    <p className="font-serif-display text-2xl italic">Your fitting will appear here.</p>
                    <p className="text-sm mt-2">Upload a photo and render to begin.</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {result && (
              <div className="flex gap-3 mt-4">
                <a
                  data-testid="download-result"
                  href={result}
                  download={`maison-verlaine-${id}.png`}
                  className="flex-1 inline-flex items-center justify-center gap-2 border border-[#1A1A1A]/20 py-3 rounded-sm text-sm hover:border-[#6D214F] hover:text-[#6D214F] transition-colors"
                >
                  <Download className="w-4 h-4" /> Save image
                </a>
                <button
                  data-testid="regenerate"
                  onClick={generate}
                  className="flex-1 inline-flex items-center justify-center gap-2 border border-[#1A1A1A]/20 py-3 rounded-sm text-sm hover:border-[#6D214F] hover:text-[#6D214F] transition-colors"
                >
                  <RefreshCw className="w-4 h-4" /> Render again
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
